import type { Station } from './stations';

/**
 * Kuriran nabor, ki se naloži ob prvem zagonu, da aplikacija ni prazna
 * (T2 §9). V celoti izbrisljiv z enim dejanjem in se po brisanju **ne vrača**
 * — za to skrbi `seedDismissed` v nastavitvah.
 *
 * Izbor ni poljuben. **Seed je hkrati testna naprava:** pokriti mora obe
 * poti toka, vse tri kodeke, obe smeri latence in tri vire metapodatkov, sicer
 * testi tveganja iz D1 in kaskada iz faze 3 nimajo česa izmeriti.
 *
 *   HTTPS direktno        16 postaj — privzeta pot
 *   samo HTTP prek proxya  2 postaji — edina pot, ki proxy res rabi
 *   HLS                    2 postaji — pot, ki proxyja **ne prenese** (faza 6)
 *   AAC · AAC+ · MP3       vsi trije kodeki, 56–320 kbps
 *   SI·HR·IT·FR·GB·NL·CH·DE·US·AU  test latence, 10 držav
 *   AzuraCast · Icecast · ICY  vsi trije živi členi kaskade (T3 §3)
 *
 * ⚖️ **Uravnoteženo za širše občinstvo 2026-07-24.** Prej je bil seed pretežno
 * slovenski (13/24). Aplikacija ni le za primarnega uporabnika, zato je zdaj
 * mednarodno uravnotežen (7 SI od 20), a **vsa testna sidra ostajajo**:
 * Aktual Dalmacija (tveganje 3), ABC Jazz (tveganje 5, najnižji bitrate),
 * METEOR (AzuraCast + `stripRepeatedArtist`), obe poti toka, obe HLS.
 * To ni izguba pokritosti — sidra so izbrana po lastnosti, ne po državi.
 * Seed je vseeno izbrisljiv, iskanje pa doda karkoli; privzetek ni zapor.
 *
 * ⚠️ **Zahteva »vse HTTPS« je bila umaknjena** (T2 §9): slovenske komercialne
 * postaje so skoraj brez izjeme samo HTTP. Zahtevati HTTPS bi pomenilo seed
 * brez postaj, ki jih primarni uporabnik dejansko posluša. Namesto tega so
 * jasno označene in tečejo prek proxyja.
 *
 * ⚠️ **Naslovi so preverjeni končni, ne tisti iz kataloga.** Radio Aktual
 * Dalmacija je v katalogu na golem IP `195.95.158.131`, ki je z **Cloudflarovega
 * roba nedosegljiv** (T2 §9): z domačega omrežja odgovori v 18 ms, iz Workerja
 * nikoli. Sonda mu ne more slediti in proxy ga ne more doseči — postaja
 * preprosto ne bi igrala. Test `nobena seed postaja ne kaže na goli IP naslov`
 * to čuva.
 *
 * ⚠️ **Vsak nov seed preveri proti DEPLOYANEMU Workerju**, ne le lokalno:
 * `wrangler dev` gre skozi tvoje omrežje in daje lažno zeleno luč.
 *
 * Vseh 20 naslovov preverjeno **2026-07-24 proti produkcijskemu `/api/stream`**
 * (rob dejansko potegne upstream): vse vrnejo `200` + audio content-type. Obe
 * HLS dodatno preverjeni na CORS **playliste IN segmenta** (hls.js ju vleče
 * sam). BBC World Service je bil kandidat, a pade (410, Akamai poti rotirajo) —
 * zamenjan z Deutschlandfunkom.
 */

/** Fiksen datum vnosa: seed mora biti determinističen, sicer testi plavajo. */
const SEEDED_AT = Date.UTC(2026, 6, 19);

/**
 * Žanri so **v angleščini**, ker jih tako pošilja radio-browser (faza 4).
 * Prevedeni seed bi ustvaril dvojezičen slovar žanrov, v katerem `rock` in
 * `rok` ne bi bila ista stvar in filter ne bi našel obojega.
 *
 * Države so **samo `countryCode`**: ime se izpiše prek `Intl.DisplayNames`
 * v jeziku vmesnika (P4 §7). Zapisano ime bi bilo v enem od obeh jezikov vedno
 * tuje.
 */
interface SeedInput {
  id: string;
  name: string;
  url: string;
  tags: string[];
  codec: string;
  bitrate: number;
  countryCode: string;
  homepage?: string;
  /** Privzeto `false`; HLS postaje gredo po drugi poti (glej `resolveStreamRoute`). */
  isHls?: boolean;
}

const SEED_INPUT: SeedInput[] = [
  /* ── Slovenija · javni servis · HTTPS, direktno ──────────────────── */
  {
    // Privzeta postaja `[0]`: čist HTTPS AAC, ki zaigra takoj. Playback testi
    // se pripenjajo na `SEED_STATIONS[0]`, zato naj ostane prva in HTTPS.
    id: 'seed-val202',
    name: 'Val 202',
    url: 'https://mp3.rtvslo.si/val202',
    tags: ['pop', 'talk'],
    codec: 'AAC',
    bitrate: 192,
    countryCode: 'SI',
    homepage: 'https://val202.rtvslo.si/',
  },

  /* ── Slovenija · komercialne · HTTPS prek preusmeritve ───────────── */
  {
    // 🔴 Edina obvezna postaja: test tveganja 3 (zatikanje v avtu) se poganja
    // na njej, ne na poljubni. Bila je edina trdo kodirana privzeta postaja
    // stare aplikacije (`js/stations.js:11`) — in tista, pri kateri se je zvok
    // zatikal (T2 §12). V katalogu je na golem IP, tu je preverjen končni
    // naslov `live.radio.si`; test se pripne na id `seed-aktual-dalmacija`.
    id: 'seed-aktual-dalmacija',
    name: 'Radio Aktual Dalmacija',
    url: 'https://live.radio.si/AktualDalmacija',
    tags: ['pop', 'folk'],
    codec: 'MP3',
    bitrate: 128,
    countryCode: 'SI',
  },

  /* ── Slovenija · resnično samo HTTP · prek proxyja ───────────────── */
  // Dve postaji držita pot proxyja (in člen ICY kaskade) živo. Obe sta samo
  // HTTP: HTTPS na istih vratih ne odgovori, preusmeritve ni.
  {
    id: 'seed-hitradio-center',
    name: 'Hitradio Center',
    url: 'http://stream3.radiocenter.si:8000/center',
    tags: ['pop'],
    codec: 'MP3',
    bitrate: 128,
    countryCode: 'SI',
  },
  {
    id: 'seed-mister-deejay',
    name: 'Radio Mister Deejay',
    url: 'http://stream.mister-deejay.com:9008/',
    tags: ['dance', 'electronic'],
    codec: 'MP3',
    bitrate: 128,
    countryCode: 'SI',
  },

  /* ── AzuraCast · SSE metapodatki (T3 §3, prvi člen kaskade) ──────── */
  {
    // ⚠️ Ta postaja je razlog, da `stripRepeatedArtist()` obstaja: njen
    // `song.text` je »Chris Isaak - Chris Isaak - Blue Hotel«. Ločeni polji
    // (`song.artist`/`song.title`) sta čisti, kar je natanko razlog, da jima
    // kaskada verjame prej kot sestavljenemu nizu.
    id: 'seed-meteor',
    name: 'Radio METEOR',
    url: 'https://stream.radiometeor.si/listen/meteor/live.mp3',
    tags: ['pop'],
    codec: 'MP3',
    bitrate: 192,
    countryCode: 'SI',
    homepage: 'https://radiometeor.si/',
  },
  {
    // Drugi AzuraCast (AAC prek SSE) in soseščina — HR.
    id: 'seed-radiomax',
    name: 'Radio Max 99.3',
    url: 'https://azuracast.novi-net.net/listen/radiomax/radiomax.aac',
    tags: ['pop'],
    codec: 'AAC',
    bitrate: 128,
    countryCode: 'HR',
  },

  /* ── Mednarodne · HTTPS, direktno ────────────────────────────────── */
  {
    // Icecast JSON — vir za kaskado metapodatkov v fazi 3 (T3 §3).
    id: 'seed-somafm-groovesalad',
    name: 'SomaFM Groove Salad',
    url: 'https://ice1.somafm.com/groovesalad-128-mp3',
    tags: ['electronic', 'ambient'],
    codec: 'MP3',
    bitrate: 128,
    countryCode: 'US',
    homepage: 'https://somafm.com/groovesalad/',
  },
  {
    // 🔴 Najnižji bitrate v seedu (AAC+ 56) — test tveganja 5 iz D1.
    // ⚠️ Naslov iz T2 (`live-radio01.mediahubaustralia.com/2TJW/mp3/`) je
    // 2026-07-19 preusmerjal na **triple j NSW**, ne na ABC Jazz; zapisan je
    // torej preverjen končni naslov. Popravljeno tudi v T2 §9.
    id: 'seed-abc-jazz',
    name: 'ABC Jazz',
    url: 'https://abc.streamguys1.com/live/abcjazz/icecast.audio',
    tags: ['jazz'],
    codec: 'AAC+',
    bitrate: 56,
    countryCode: 'AU',
  },
  {
    id: 'seed-kexp',
    name: 'KEXP Seattle',
    url: 'https://kexp.streamguys1.com/kexp160.aac',
    tags: ['rock', 'indie'],
    codec: 'AAC',
    bitrate: 160,
    countryCode: 'US',
    homepage: 'https://www.kexp.org/',
  },
  {
    id: 'seed-classic-fm',
    name: 'Classic FM',
    url: 'https://media-ssl.musicradio.com/ClassicFMMP3',
    tags: ['classical'],
    codec: 'MP3',
    bitrate: 128,
    countryCode: 'GB',
    homepage: 'https://www.classicfm.com/',
  },
  {
    id: 'seed-npo-radio2',
    name: 'NPO Radio 2',
    url: 'https://icecast.omroep.nl/radio2-bb-mp3',
    tags: ['pop'],
    codec: 'MP3',
    bitrate: 192,
    countryCode: 'NL',
  },
  {
    // FIP — eklektika Radio France; Icecast, dodatni vir za kaskado. Preverjeno
    // 2026-07-24: `fip-hifi.aac` vrne 200 audio/aac z roba.
    id: 'seed-fip',
    name: 'FIP',
    url: 'https://icecast.radiofrance.fr/fip-hifi.aac',
    tags: ['eclectic'],
    codec: 'AAC',
    bitrate: 192,
    countryCode: 'FR',
    homepage: 'https://www.radiofrance.fr/fip',
  },
  {
    // Najvišji bitrate v seedu (AAC 320) — zgornji rob razpona.
    id: 'seed-radio-paradise',
    name: 'Radio Paradise',
    url: 'https://stream.radioparadise.com/aac-320',
    tags: ['eclectic', 'rock'],
    codec: 'AAC',
    bitrate: 320,
    countryCode: 'US',
    homepage: 'https://radioparadise.com/',
  },
  {
    // ⚠️ `stream.srg-ssr.ch` je razdelilnik (302 na `livestreaming-node-N`).
    // V seed gre **stabilni vstopni naslov**, ne razrešeni vozel — `<audio>`
    // sledi preusmeritvi sam, vozel se sme spremeniti. +CH latenca.
    id: 'seed-radio-swiss-jazz',
    name: 'Radio Swiss Jazz',
    url: 'https://stream.srg-ssr.ch/m/rsj/mp3_128',
    tags: ['jazz'],
    codec: 'MP3',
    bitrate: 128,
    countryCode: 'CH',
    homepage: 'https://www.radioswissjazz.ch/',
  },
  {
    // ⚠️ Kot laut.fm: vstopni naslov 302-preusmeri na **podpisan, časovno
    // omejen** CDN naslov (`d131.rndfnk.com/…?token=…`). Hranimo VSTOPNI
    // naslov; razrešenega se ne sme shraniti v `urlResolved` (čez minute ne
    // velja). HTTPS je, zato se ga sonda iz §11 ne dotakne. Zamenjava za BBC
    // World Service (ta pade z 410, ker Akamai poti rotirajo).
    id: 'seed-deutschlandfunk',
    name: 'Deutschlandfunk',
    url: 'https://st01.sslstream.dlf.de/dlf/01/128/mp3/stream.mp3',
    tags: ['news', 'talk'],
    codec: 'MP3',
    bitrate: 128,
    countryCode: 'DE',
    homepage: 'https://www.deutschlandfunk.de/',
  },
  {
    // ⚠️ `stream-relay-geo.ntslive.net` je geo-usmerjen relay (302 na najbližji
    // radiomast vozel). Hranimo stabilni vstopni naslov; usmeritev je namen.
    id: 'seed-nts1',
    name: 'NTS Radio 1',
    url: 'https://stream-relay-geo.ntslive.net/stream',
    tags: ['eclectic', 'electronic'],
    codec: 'MP3',
    bitrate: 128,
    countryCode: 'GB',
    homepage: 'https://www.nts.live/',
  },

  /* ── HLS ─────────────────────────────────────────────────────────── */
  // Dodani 2026-07-21, ker faza 6 brez njiju **ni imela česa izmeriti**: HLS
  // predvajalnik bi bil napisan po specifikaciji in nikoli slišan (D3 #20).
  //
  // Merilo za izbor ni bila priljubljenost, ampak **CORS na playlisti IN na
  // segmentih**: `hls.js` segmente prenaša sam, prek `fetch`, zato playlista s
  // CORS in segmenti brez njega ne zadošča. Preverjeno na obeh, na obojem
  // (znova 2026-07-24: playlist + segment vrneta 200 + ACAO).
  //
  // ⛔ **Obe morata biti HTTPS in to ni naključje.** HLS proxyja ne prenese
  // (`resolveStreamRoute`), torej za mešano vsebino ni poti okoli — postaja
  // brez HTTPS bi bila v seedu mrtva.
  {
    // CORS odgovori z **odmevom izvora**, ne z `*`. Deluje, a je vezan na
    // našega: če se domena kdaj spremeni, se to pokaže tukaj prvo.
    id: 'seed-france-inter',
    name: 'France Inter',
    url: 'https://stream.radiofrance.fr/franceinter/franceinter_hifi.m3u8',
    tags: ['talk', 'news'],
    codec: 'AAC',
    bitrate: 192,
    countryCode: 'FR',
    homepage: 'https://www.radiofrance.fr/franceinter',
    isHls: true,
  },
  {
    // Akamai, CORS `*`. Glasbena — ob preverjanju HLS je to tista, ki jo je
    // mogoče poslušati dovolj dolgo, da se izpad sliši.
    id: 'seed-radio-italia-smi',
    name: 'Radio Italia SMI',
    url: 'https://radioitaliasmi.akamaized.net/hls/live/2093120/RISMI/stream01/streamPlaylist.m3u8',
    tags: ['pop'],
    codec: 'AAC',
    bitrate: 96,
    countryCode: 'IT',
    homepage: 'https://www.radioitalia.it/',
    isHls: true,
  },

  /* ── Slovenija · okus (edina SI za svoj žanr) ────────────────────── */
  {
    id: 'seed-ognjisce',
    name: 'Radio Ognjišče',
    url: 'https://live.radio.si/ognjisce.mp3',
    tags: ['talk', 'religious'],
    codec: 'MP3',
    bitrate: 192,
    countryCode: 'SI',
  },
  {
    id: 'seed-rock-radio',
    name: 'Rock Radio',
    url: 'https://stream.nextmedia.si/proxy/rockr1_1?mp=/rock',
    tags: ['rock'],
    codec: 'MP3',
    bitrate: 128,
    countryCode: 'SI',
  },
];

export const SEED_STATIONS: readonly Station[] = SEED_INPUT.map((input, index) => {
  const station: Station = {
    id: input.id,
    name: input.name,
    url: input.url,
    tags: input.tags,
    codec: input.codec,
    bitrate: input.bitrate,
    countryCode: input.countryCode,
    isHls: input.isHls === true,
    isHttps: input.url.startsWith('https:'),
    favorite: false,
    order: index,
    addedAt: SEEDED_AT,
    playCount: 0,
    source: 'seed',
  };

  if (input.homepage !== undefined) station.homepage = input.homepage;
  return station;
});
